// Pure helpers for the review step of an import: what the dry run says, grouped and worded for people.
// No React in here, so the rules can be tested without rendering.

const skippedActions = ['FileNotFound', 'DocumentNotFound'];

const stateOfAction = (action) => {
  if (skippedActions.includes(action))
    return 'skipped';
  return action.charAt(0).toLowerCase() + action.slice(1);
};

// Only a connection the import writes is "imported with a warning"; an unchanged one stays as it is.
const warnsOnImport = (r) => r.warnings.length > 0 && (r.state === 'create' || r.state === 'update');

// The server says why a row is removed. Only a document the export lists without a connection can be kept.
export const listedWithoutConnection = 'NoConnectionInExport';

const countsOf = (rows) => ({
  skipped: rows.filter(r => r.state === 'skipped').length,
  remove: rows.filter(r => r.state === 'remove').length,
  kept: rows.filter(r => r.state === 'kept').length,
  warnings: rows.filter(warnsOnImport).length,
  create: rows.filter(r => r.state === 'create').length,
  update: rows.filter(r => r.state === 'update').length,
  unchanged: rows.filter(r => r.state === 'unchanged').length,
});

const toRows = (plan, removeUnconnected) => [
  ...plan.items.map(i => ({
    fileName: i.fileName,
    documentName: i.documentName,
    action: i.action,
    state: stateOfAction(i.action),
    changedFields: i.changedFields || [],
    warnings: i.warnings || [],
  })),
  ...plan.removals.map(r => {
    const kept = !removeUnconnected && r.reason === listedWithoutConnection;
    return {
      fileName: r.fileName,
      documentName: r.documentName,
      action: kept ? 'Keep' : 'Remove',
      state: kept ? 'kept' : 'remove',
      reason: r.reason,
      changedFields: [],
      warnings: [],
    };
  }),
];

// What the toggle is about: documents the export lists without a connection, and the target connections it would remove.
const unconnectedOf = (plan) => ({
  documents: (plan.files || []).reduce((sum, f) => sum + (f.unconnectedDocumentCount || 0), 0),
  files: (plan.files || []).filter(f => f.unconnectedDocumentCount > 0).length,
  connections: plan.removals.filter(r => r.reason === listedWithoutConnection).length,
});

export const summarise = (plan, { removeUnconnected = true } = {}) => {
  const rows = toRows(plan, removeUnconnected);
  const counts = countsOf(rows);
  return {
    ...counts,
    files: new Set(rows.map(r => r.fileName)).size,
    unconnected: unconnectedOf(plan),
    // Nothing lost and nothing skipped: the one case where the page can say so outright.
    cleanBill: counts.skipped === 0 && counts.remove === 0,
  };
};

export const groupByLogicFile = (plan, { filter = null, query = '', removeUnconnected = true } = {}) => {
  const rows = toRows(plan, removeUnconnected);
  const filesByName = new Map((plan.files || []).map(f => [f.fileName, f]));
  const byFile = new Map();
  rows.forEach(r => {
    if (!byFile.has(r.fileName))
      byFile.set(r.fileName, []);
    byFile.get(r.fileName).push(r);
  });

  const needle = query.trim().toLowerCase();
  const matchesFilter = (r) => !filter || (filter === 'warnings' ? warnsOnImport(r) : r.state === filter);

  const groups = [...byFile.entries()].map(([fileName, all]) => {
    const counts = countsOf(all);
    const fileMatches = needle !== '' && fileName.toLowerCase().includes(needle);
    const shown = all
      .filter(matchesFilter)
      .filter(r => needle === '' || fileMatches || r.documentName.toLowerCase().includes(needle));
    const planFile = filesByName.get(fileName) || {};
    return {
      fileName,
      fileNotFound: all.every(r => r.action === 'FileNotFound'),
      counts,
      // Documents with a connection in the target: a create has none yet, a skipped row is not in the target.
      // A removal whose document left the logic file still counts, so this can exceed the document total.
      documents: planFile.documentCount || 0,
      withConnection: counts.unchanged + counts.update + counts.remove + counts.kept,
      changing: counts.create + counts.update + counts.remove,
      needsAttention: counts.skipped > 0 || counts.remove > 0 || counts.warnings > 0,
      defaultOpen: all.some(r => r.state !== 'unchanged'),
      rows: shown,
    };
  }).filter(g => g.rows.length > 0);

  // Stable, so files keep the order of the export within each of the two halves.
  return [...groups.filter(g => g.needsAttention), ...groups.filter(g => !g.needsAttention)];
};

const fieldLabels = {
  PreProcessWorkflowId: 'pre-process workflow',
  PrePostProcessWorkflowId: 'pre/post-process workflow',
  PostProcessWorkflowId: 'post-process workflow',
  SmartformId: 'SmartForm',
  DistributionListId: 'distribution list',
  TextLibraryFolders: 'text library folders',
  ForceToRunSmartformBeforeEditorSession: 'SmartForm timing',
  IsAttachment: 'attachment setting',
  PdfAttachmentPolicy: 'PDF setting',
  DocumentId: 'document link',
  DocumentName: 'document link',
  DocumentDescription: 'document link',
  TemplateFileName: 'document link',
};

const joinWords = (words) => (words.length < 2 ? words.join('') : `${words.slice(0, -1).join(', ')} and ${words[words.length - 1]}`);

const kindTitles = { Workflow: 'Workflow', Smartform: 'SmartForm', DistributionList: 'Distribution list', TextLibraryFolder: 'Text library folder' };
const kindNouns = { Workflow: 'workflow', Smartform: 'SmartForm', DistributionList: 'distribution list', TextLibraryFolder: 'text library folder' };

const warningText = (w, env, written) => {
  const noun = kindNouns[w.kind] || w.kind;
  const subject = `${kindTitles[w.kind] || w.kind} ${w.referenceName ? `"${w.referenceName}"` : w.referenceId}`;
  const problems = {
    NotFound: [`is not in ${env}`, `Create the ${noun} in ${env}.`],
    NotPromotedToEnvironment: [`is not promoted to ${env}`, `Promote the ${noun} to ${env}.`],
    TagMismatch: ['is the wrong type', `Use a ${noun} of the right type.`],
    Invalid: ['is invalid', `Check the ${noun} in ${env}.`],
  };
  const [problem, fix] = problems[w.status] || [`reports ${w.status} in ${env}`, `Check the ${noun} in ${env}.`];
  return { text: `${written ? 'Imported as it is. ' : ''}${subject} ${problem}.`, fix };
};

export const whatHappens = (row, env) => {
  const written = row.state === 'create' || row.state === 'update';
  const warnings = (row.warnings || []).map(w => warningText(w, env, written));
  switch (row.state) {
    case 'create':
      return { text: `Has no connection in ${env}. The connection from the export is added.`, fix: null, warnings };
    case 'update': {
      const labels = [...new Set(row.changedFields.map(f => fieldLabels[f] || f))];
      return { text: `Changes the ${joinWords(labels)}.`, fix: null, warnings };
    }
    case 'unchanged':
      return { text: 'Already identical. Nothing happens.', fix: null, warnings };
    case 'remove':
      return {
        text: row.reason === listedWithoutConnection
          ? `Listed in the export without a connection. The connection in ${env} is removed.`
          : `Not in the export. The connection in ${env} is removed.`,
        fix: null,
        warnings,
      };
    case 'kept':
      return { text: `Listed in the export without a connection. The connection in ${env} is kept.`, fix: null, warnings };
    default:
      return row.action === 'FileNotFound'
        ? { text: `The logic file is not in ${env}. The connection is skipped.`, fix: 'Upload the logic file, then import again.', warnings }
        : { text: `This document is not in the logic file in ${env}. The connection is skipped.`, fix: 'Upload the logic file with this document, then import again.', warnings };
  }
};
